import { useEffect, useRef } from "react";
import type { DockSurfaceState, PopupSurfaceState } from "@/surfaces/SurfaceTypes";
import type {
	EngagementModalKind,
	EngagementProvider,
} from "@/provider/EngagementProvider";
import { debugLog } from "@/utils";

export type ActiveDockTarget = "mission" | "tournament";

function isCaptainUpKind(kind: ActiveDockTarget): kind is EngagementModalKind {
	return kind === "mission" || kind === "tournament";
}

function otherCaptainUpKind(kind: EngagementModalKind): EngagementModalKind {
	return kind === "mission" ? "tournament" : "mission";
}

type DockHostMap = Partial<Record<ActiveDockTarget, HTMLDivElement | null>>;

type AppliedDockCommand =
	| {
			type: "park_all";
			parkingHost: HTMLElement;
	  }
	| {
			type: "place_active";
			activeKind: EngagementModalKind;
			activeHost: HTMLElement;
			inactiveKind: EngagementModalKind;
			parkingHost: HTMLElement;
	  };

type AppliedModalPlacement =
	| {
			type: "attached";
			modalHost: HTMLElement;
	  }
	| {
			type: "parked";
			parkingHost: HTMLElement;
	  }
	| {
			type: "opening_wait_grant";
	  }
	| {
			type: "granted_hidden_wait_exit_ack";
	  }
	| {
			type: "inactive_without_parking_host";
	  };

export type CaptainUpIntegrationInputs = {
	provider: EngagementProvider | null;
	activeDockTarget: ActiveDockTarget;
	dock: DockSurfaceState | null;
	popup: PopupSurfaceState | null;
	dockHosts: DockHostMap;
	suppressNextProtocolCloseToken?: number;
};

function isSameAppliedDockCommand(
	current: AppliedDockCommand | null,
	next: AppliedDockCommand
): boolean {
	if (!current) {
		return false;
	}

	if (current.type !== next.type) {
		return false;
	}

	if (current.type === "park_all" && next.type === "park_all") {
		return current.parkingHost === next.parkingHost;
	}

	if (current.type === "place_active" && next.type === "place_active") {
		return (
			current.activeKind === next.activeKind &&
			current.activeHost === next.activeHost &&
			current.inactiveKind === next.inactiveKind &&
			current.parkingHost === next.parkingHost
		);
	}

	return false;
}

function isSameAppliedModalPlacement(
	current: AppliedModalPlacement | null,
	next: AppliedModalPlacement
): boolean {
	if (!current) {
		return false;
	}

	if (current.type !== next.type) {
		return false;
	}

	if (current.type === "attached" && next.type === "attached") {
		return current.modalHost === next.modalHost;
	}

	if (current.type === "parked" && next.type === "parked") {
		return current.parkingHost === next.parkingHost;
	}

	return true;
}

export function useCaptainUpIntegration({
	provider,
	activeDockTarget,
	dock,
	popup,
	dockHosts,
	suppressNextProtocolCloseToken = 0,
}: CaptainUpIntegrationInputs): void {
	const openedModalKindRef = useRef<EngagementModalKind | null>(null);
	const lastConsumedSuppressCloseTokenRef = useRef(0);
	const lastAppliedDockCommandRef = useRef<AppliedDockCommand | null>(null);
	const lastAppliedModalPlacementRef = useRef<AppliedModalPlacement | null>(
		null
	);

	useEffect(() => {
		debugLog(
			"[CaptainUpIntegration] provider changed -> resetting local integration state",
			{
				hasProvider: !!provider,
			}
		);

		openedModalKindRef.current = null;
		lastConsumedSuppressCloseTokenRef.current = 0;
		lastAppliedDockCommandRef.current = null;
		lastAppliedModalPlacementRef.current = null;
	}, [provider]);

	useEffect(() => {
		if (!provider || !dock?.parkingHost) {
			return;
		}

		const applyDockCommand = (
			nextCommand: AppliedDockCommand,
			reason: string
		) => {
			if (
				isSameAppliedDockCommand(
					lastAppliedDockCommandRef.current,
					nextCommand
				)
			) {
				debugLog(
					"[CaptainUpIntegration] dock command skipped because target is unchanged",
					{
						reason,
						nextCommand,
					}
				);
				return;
			}

			if (nextCommand.type === "park_all") {
				debugLog("[CaptainUpIntegration] dock -> parkAllDocks()", {
					reason,
					parkingHostPresent: !!nextCommand.parkingHost,
				});
				provider.parkAllDocks(nextCommand.parkingHost);
			} else {
				debugLog(
					"[CaptainUpIntegration] dock -> attach active and park inactive",
					{
						reason,
						activeKind: nextCommand.activeKind,
						inactiveKind: nextCommand.inactiveKind,
					}
				);
				provider.attachDock(nextCommand.activeKind, nextCommand.activeHost);
				provider.parkDock(nextCommand.inactiveKind, nextCommand.parkingHost);
			}

			lastAppliedDockCommandRef.current = nextCommand;
		};

		/*
		 * Dock orchestration must not detach vendor dock DOM merely because the
		 * host surface becomes temporarily hidden during popup/fullscreen churn.
		 *
		 * Our responsibility here is DOM placement, not visibility policy.
		 * As long as:
		 * - the selected dock target is supported,
		 * - the host area exists,
		 * - the active host exists,
		 * - and the parking host exists for the inactive widget,
		 * we keep the active dock attached.
		 *
		 * This prevents popup close/open races from parking the dock and leaving
		 * it detached until some later host/layout tick happens to reattach it.
		 */
		if (!dock.hasArea) {
			applyDockCommand(
				{
					type: "park_all",
					parkingHost: dock.parkingHost,
				},
				"no_dock_area_available"
			);
			return;
		}

		if (!isCaptainUpKind(activeDockTarget)) {
			applyDockCommand(
				{
					type: "park_all",
					parkingHost: dock.parkingHost,
				},
				"active_target_not_supported_by_captainup"
			);
			return;
		}

		const activeKind = activeDockTarget;
		const inactiveKind = otherCaptainUpKind(activeKind);
		const activeHost = dockHosts[activeKind] ?? null;

		if (!activeHost) {
			applyDockCommand(
				{
					type: "park_all",
					parkingHost: dock.parkingHost,
				},
				"missing_active_dock_host"
			);
			return;
		}

		applyDockCommand(
			{
				type: "place_active",
				activeKind,
				activeHost,
				inactiveKind,
				parkingHost: dock.parkingHost,
			},
			dock.visible
				? "active_dock_ready_visible"
				: "active_dock_ready_hidden_surface_only"
		);
	}, [provider, activeDockTarget, dock, dockHosts]);

	useEffect(() => {
		if (!provider || !popup) {
			return;
		}

		const applyModalPlacement = (
			nextPlacement: AppliedModalPlacement,
			reason: string
		) => {
			if (
				isSameAppliedModalPlacement(
					lastAppliedModalPlacementRef.current,
					nextPlacement
				)
			) {
				debugLog(
					"[CaptainUpIntegration] modal placement skipped because target is unchanged",
					{
						reason,
						nextPlacement,
					}
				);
				return;
			}

			if (nextPlacement.type === "attached") {
				debugLog("[CaptainUpIntegration] modal -> attachModal()", {
					reason,
					hasModalHost: !!nextPlacement.modalHost,
				});
				provider.attachModal(nextPlacement.modalHost);
			} else if (nextPlacement.type === "parked") {
				debugLog("[CaptainUpIntegration] modal -> parkModal()", {
					reason,
					hasParkingHost: !!nextPlacement.parkingHost,
				});
				provider.parkModal(nextPlacement.parkingHost);
			} else {
				debugLog(
					"[CaptainUpIntegration] modal placement updated without provider command",
					{
						reason,
						nextPlacement,
					}
				);
			}

			lastAppliedModalPlacementRef.current = nextPlacement;
		};

		debugLog("[CaptainUpIntegration] popup orchestration tick", {
			activeDockTarget,
			popupVisible: popup.visible,
			popupGranted: popup.granted,
			hasModalHost: !!popup.modalHost,
			hasParkingHost: !!popup.parkingHost,
			openedModalKind: openedModalKindRef.current,
			suppressNextProtocolCloseToken,
			lastConsumedSuppressCloseToken: lastConsumedSuppressCloseTokenRef.current,
		});

		if (popup.granted && popup.modalHost) {
			applyModalPlacement(
				{
					type: "attached",
					modalHost: popup.modalHost,
				},
				"fullscreen_granted_with_modal_host"
			);
		}

		if (!popup.granted) {
			if (popup.visible) {
				applyModalPlacement(
					{
						type: "opening_wait_grant",
					},
					"popup_visible_while_waiting_for_fullscreen_grant"
				);
				return;
			}

			if (popup.parkingHost) {
				applyModalPlacement(
					{
						type: "parked",
						parkingHost: popup.parkingHost,
					},
					"popup_inactive_and_not_granted"
				);
			} else {
				applyModalPlacement(
					{
						type: "inactive_without_parking_host",
					},
					"popup_inactive_without_parking_host"
				);
			}

			const shouldSuppressCloseEcho =
				suppressNextProtocolCloseToken >
				lastConsumedSuppressCloseTokenRef.current;

			if (shouldSuppressCloseEcho) {
				lastConsumedSuppressCloseTokenRef.current =
					suppressNextProtocolCloseToken;

				debugLog(
					"[CaptainUpIntegration] suppressing inactive-state cleanup echo because close originated from CaptainUp",
					{
						openedModalKind: openedModalKindRef.current,
						suppressNextProtocolCloseToken,
					}
				);
			} else {
				debugLog(
					"[CaptainUpIntegration] popup fully inactive -> clearing local opened modal tracking without issuing vendor close commands",
					{
						previousOpenedModalKind: openedModalKindRef.current,
					}
				);
			}

			openedModalKindRef.current = null;
			return;
		}

		if (!popup.visible) {
			applyModalPlacement(
				{
					type: "granted_hidden_wait_exit_ack",
				},
				"fullscreen_granted_but_popup_hidden_waiting_for_exit_ack"
			);
			return;
		}

		if (!isCaptainUpKind(activeDockTarget)) {
			debugLog(
				"[CaptainUpIntegration] popup visible but active dock target is not CaptainUp",
				{
					activeDockTarget,
				}
			);
			return;
		}

		if (openedModalKindRef.current === activeDockTarget) {
			debugLog(
				"[CaptainUpIntegration] popup visible for same modal kind -> keeping current modal state",
				{
					activeDockTarget,
				}
			);
			return;
		}

		debugLog(
			"[CaptainUpIntegration] popup visible -> enforce exclusive modal selection",
			{
				previousOpenedKind: openedModalKindRef.current,
				nextOpenedKind: activeDockTarget,
			}
		);

		openedModalKindRef.current = activeDockTarget;
		provider.ensureExclusiveModal(activeDockTarget);
	}, [provider, activeDockTarget, popup, suppressNextProtocolCloseToken]);
}

export default useCaptainUpIntegration;